import { useDiagramStore } from '../stores/diagramStore';
import type { Node, SequenceParticipant } from '../types';

function matchesLabel(value: string, query: string): boolean {
  return value.toLowerCase() === query.toLowerCase();
}

/**
 * Resolve a node by exact id first, then unique case-insensitive label.
 * Duplicate labels return an error instead of silently picking the first match.
 */
export function resolveNode(labelOrId: string): Node | { error: string } {
  const { nodes } = useDiagramStore.getState();
  const byId = nodes.find(n => n.id === labelOrId);
  if (byId) return byId;

  const byLabel = nodes.filter(n => matchesLabel(n.label, labelOrId));
  if (byLabel.length === 1) return byLabel[0];
  if (byLabel.length > 1) {
    return {
      error: `Ambiguous node label "${labelOrId}" matches ${byLabel.length} nodes (${byLabel.map(n => n.id).join(', ')}). Use an id.`,
    };
  }
  return { error: `Node not found: ${labelOrId}` };
}

export function resolveParticipant(labelOrId: string): SequenceParticipant | { error: string } {
  const { participants } = useDiagramStore.getState();
  const byId = participants.find(p => p.id === labelOrId);
  if (byId) return byId;

  const byLabel = participants.filter(p => matchesLabel(p.label, labelOrId));
  if (byLabel.length === 1) return byLabel[0];
  if (byLabel.length > 1) {
    return {
      error: `Ambiguous participant label "${labelOrId}" matches ${byLabel.length} participants. Use an id.`,
    };
  }
  return { error: `Participant not found: ${labelOrId}` };
}

export function isResolveError<T>(value: T | { error: string }): value is { error: string } {
  return typeof value === 'object' && value !== null && 'error' in value;
}

export function resolveSocket(
  node: Node,
  ref: number | string | undefined,
  direction: 'input' | 'output',
): string | null {
  if (ref === undefined) {
    return node.sockets.find(s => s.type === direction)?.id ?? null;
  }
  if (typeof ref === 'number') {
    return node.sockets.filter(s => s.type === direction).find(s => (s.order ?? 0) === ref)?.id ?? null;
  }
  return String(ref);
}

export function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

export function asPoint(value: unknown): { x: number; y: number } | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const rec = value as { x?: unknown; y?: unknown };
  if (typeof rec.x === 'number' && typeof rec.y === 'number') return { x: rec.x, y: rec.y };
  return undefined;
}
